import { useParams, useNavigate } from 'react-router-dom';
import { useCreateActivitySheet } from '@/hooks/useCreateActivitySheet';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { useProducer } from '@/hooks/useProducer';
import { useProducerEvents, useDeleteEvent, useUpdateEvent, useProducerEvent } from '@/hooks/useProducerEvents';
import { useCloneActivity } from '@/hooks/useCloneActivity';
import { isPastActivity } from '@/lib/events/isPastActivity';
import { supabase } from '@/integrations/supabase/client';

import { useProducerUsage } from '@/hooks/useProducerUsage';
import { ProducerLayout, FeatureBlockedBanner } from '@/components/producer';
import { SoftLimitMessage } from '@/components/producer/SoftLimitMessage';
import { ProducerEventCard } from '@/components/producer/ProducerEventCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, MapPin, Heart, Eye, EyeOff, Clock, AlertTriangle, Pencil, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useState, useMemo } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { isPending } from '@/lib/producer/status';
import { normalizeActivityType, type ActivityType } from '@/types/activity';
import { getActivityTypeLabel } from '@/lib/activities/labels';
import { getActivityEditHref } from '@/lib/activities/routes';
import { EventActionsMenu } from '@/components/events/EventActionsMenu';
import { getTimeStatus } from '@/lib/outings/timeStatus';
import { formatOutingDateLabel } from '@/lib/outings/utils';
import { getEventTimeBucket } from '@/lib/events/timeBucket';
import { useNowTick } from '@/hooks/useNowTick';
import { producerActivitiesEmbedPath, producerPublicPath } from '@/lib/paths';
import { EntityShareAction } from '@/features/share/EntityShareAction';

import type { ProducerEventWithBookings } from '@/types/events';

import { cn } from '@/lib/utils';

// ─── Domain types ───────────────────────────────────────────────────

/** Unified item for display in the producer work-list */
interface UnifiedEvent {
  id: string;
  title: string;
  activityType: ActivityType;
  eventTypeRaw: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  timingMode: 'date_time' | 'open' | 'anytime' | 'season';
  selectedSeasons: string[];
  shortDescription: string | null;
  updatedAt: string;
  tags: string[];
  useProducerAddress: boolean;
  customAddress: string | null;
  maxSpots: number;
  bookedSpots: number;
  isFull: boolean;
  spotsLeft: number | null;
  bookingMode?: 'none' | 'optional' | 'required';
  occurrencesCount: number;
  isFree: boolean;
  priceOre: number;
}

function eventToUnified(e: ProducerEventWithBookings): UnifiedEvent {
  const raw = e as any;
  const allTags: string[] = raw.outing_tags ?? [];
  const seasonTags = allTags.filter((t: string) => t.startsWith('season:')).map((t: string) => t.replace('season:', ''));
  const nonSeasonTags = allTags.filter((t: string) => !t.startsWith('season:'));
  return {
    id: e.id,
    title: e.title,
    activityType: normalizeActivityType(e.event_type),
    eventTypeRaw: e.event_type,
    status: e.status,
    startDate: e.start_date,
    endDate: e.end_date,
    timingMode: raw.timing_mode || 'date_time',
    selectedSeasons: seasonTags,
    shortDescription: null,
    updatedAt: e.updated_at,
    tags: nonSeasonTags,
    useProducerAddress: raw.use_producer_address ?? true,
    customAddress: raw.custom_address ?? null,
    maxSpots: e.max_spots ?? 0,
    bookedSpots: e.booked_spots ?? 0,
    isFull: e.is_full ?? false,
    spotsLeft: e.spots_left ?? null,
    bookingMode: e.booking_mode ?? 'none',
    occurrencesCount: e.occurrences_count ?? 0,
    isFree: e.is_free ?? false,
    priceOre: e.price_ore ?? 0,
  };
}


// ─── Filter chips ───────────────────────────────────────────────────
// UX rule: horizontal filter chips instead of dropdowns on mobile

// Status filter
type StatusFilter = 'all' | 'public' | 'draft' | 'upcoming' | 'past';

const STATUS_CHIPS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Alla' },
  { value: 'public', label: 'Publicerade' },
  { value: 'draft', label: 'Utkast' },
  { value: 'upcoming', label: 'Kommande' },
  { value: 'past', label: 'Passerade' },
];

// Type filter
type TypeFilter = 'all' | 'event' | 'course' | 'excursion';

const TYPE_CHIPS: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: 'Alla typer' },
  { value: 'event', label: 'Aktiviteter' },
  { value: 'course', label: 'Kurser' },
  { value: 'excursion', label: 'Utflykter' },
];

function applyFilters(items: UnifiedEvent[], status: StatusFilter, type: TypeFilter, now: Date): UnifiedEvent[] {
  let result = items;

  // Type filter
  if (type !== 'all') {
    result = result.filter(i => i.activityType === type);
  }

  // Status filter
  switch (status) {
    case 'public':
      result = result.filter(i => i.status === 'public');
      break;
    case 'draft':
      result = result.filter(i => i.status === 'draft');
      break;
    case 'upcoming':
      result = result.filter(i => getEventTimeBucket(i, now).bucket === 'upcoming');
      break;
    case 'past':
      result = result.filter(i => getEventTimeBucket(i, now).bucket === 'past');
      break;
  }

  return result;
}

/** Sort priority: upcoming (ASC) → drafts (updated DESC) → past (DESC) */
function sortActivities(items: UnifiedEvent[], now: Date, filterMode: StatusFilter): UnifiedEvent[] {
  if (filterMode === 'past') {
    // When explicitly filtering past, sort by most recent first
    return [...items].sort((a, b) => new Date(b.endDate || b.startDate).getTime() - new Date(a.endDate || a.startDate).getTime());
  }

  return [...items].sort((a, b) => {
    const bucketA = getEventTimeBucket(a, now).bucket;
    const bucketB = getEventTimeBucket(b, now).bucket;

    const groupOrder: Record<string, number> = { upcoming: 0, draft: 1, past: 2 };
    const gA = bucketA === 'upcoming' ? 0 : (bucketA === 'draft' || (bucketA !== 'past' && a.status === 'draft')) ? 1 : 2;
    const gB = bucketB === 'upcoming' ? 0 : (bucketB === 'draft' || (bucketB !== 'past' && b.status === 'draft')) ? 1 : 2;

    // Upcoming non-past drafts go to group 1
    const effectiveA = bucketA !== 'past' && a.status === 'draft' ? 1 : (bucketA === 'upcoming' ? 0 : 2);
    const effectiveB = bucketB !== 'past' && b.status === 'draft' ? 1 : (bucketB === 'upcoming' ? 0 : 2);

    if (effectiveA !== effectiveB) return effectiveA - effectiveB;

    // Within upcoming: earliest first
    if (effectiveA === 0) {
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    }
    // Within drafts: most recently updated first
    if (effectiveA === 1) {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    }
    // Within past: most recent first
    return new Date(b.endDate || b.startDate).getTime() - new Date(a.endDate || a.startDate).getTime();
  });
}

// ─── Page ───────────────────────────────────────────────────────────

export default function ProducerEventsPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const { producer, loading: producerLoading } = useProducer(farmId || '');
  const { data: events, isLoading: eventsLoading } = useProducerEvents(farmId);
  
  const { data: usage } = useProducerUsage(farmId);
  const deleteEvent = useDeleteEvent();
  const updateEvent = useUpdateEvent();
  const cloneActivity = useCloneActivity();

  const { openCreateMenu, CreateActivitySheet } = useCreateActivitySheet(farmId || '');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const [eventToUnpublish, setEventToUnpublish] = useState<UnifiedEvent | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [activeStatus, setActiveStatus] = useState<StatusFilter>('all');
  const [activeType, setActiveType] = useState<TypeFilter>('all');


  // Live tick: re-evaluates upcoming/past every 60s without full page refresh
  const now = useNowTick(60_000);

  const isProducerPending = producer ? isPending(producer.producer_status as string) : false;
  const isLoading = producerLoading || eventsLoading;

  // Show all event types (event + course + outing) in the unified list
  const unified = useMemo(() => {
    const items: UnifiedEvent[] = [];
    if (events) events.forEach(e => {
      items.push(eventToUnified(e));
    });
    return items;
  }, [events]);

  const filtered = useMemo(() => {
    const list = applyFilters(unified, activeStatus, activeType, now);
    return sortActivities(list, now, activeStatus);
  }, [unified, activeStatus, activeType, now]);

  // Counts for status summary
  const counts = useMemo(() => {
    const published = unified.filter(i => i.status === 'public').length;
    const drafts = unified.filter(i => i.status === 'draft').length;
    return { published, drafts, total: unified.length };
  }, [unified]);

  // Bookable limit (outings excluded per design)
  const bookableCount = useMemo(
    () => unified.filter(a => a.activityType === 'course' || a.activityType === 'event').length,
    [unified],
  );

  if (!farmId) return null;

  const handleDeleteClick = (id: string) => {
    // Find the item and check if past
    const item = unified.find(u => u.id === id);
    if (item && isPastActivity({ event_type: item.eventTypeRaw, timing_mode: item.timingMode, start_date: item.startDate, end_date: item.endDate })) {
      toast.error('Passerade aktiviteter kan inte tas bort.');
      return;
    }
    setEventToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!eventToDelete || !farmId) return;
    try {
      await deleteEvent.mutateAsync({ id: eventToDelete, producerId: farmId });
      toast.success('Aktiviteten borttagen');
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Kunde inte ta bort aktiviteten');
    } finally {
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    }
  };

  const handleClone = async (item: UnifiedEvent) => {
    if (!farmId) return;
    try {
      // Fetch full event for clone
      const { data: fullEvent, error } = await supabase
        .from('producer_events')
        .select('*')
        .eq('id', item.id)
        .single();
      if (error || !fullEvent) {
        toast.error('Kunde inte ladda aktiviteten');
        return;
      }
      const cloned = await cloneActivity.mutateAsync(fullEvent as any);
      toast.success('Skapade en ny aktivitet baserad på den tidigare. Kontrollera datum och tid innan du publicerar.');
      navigate(`/app/producer/farm/${farmId}/events/${cloned.id}/edit`);
    } catch (err) {
      console.error('Clone failed:', err);
      toast.error('Kunde inte skapa kopia');
    }
  };

  const handleToggleVisibility = async (item: UnifiedEvent) => {
    if (!farmId) return;
    if (isPastActivity({ event_type: item.eventTypeRaw, timing_mode: item.timingMode, start_date: item.startDate, end_date: item.endDate })) {
      toast.error('Passerade aktiviteter kan inte ändras.');
      return;
    }
    const newStatus = item.status === 'public' ? 'draft' : 'public';
    // Gate: don't allow publish if producer isn't public
    if (newStatus === 'public' && producer?.status !== 'public') {
      toast.error('Gör gården synlig först för att kunna publicera aktiviteter.');
      return;
    }
    // Confirm before unpublishing (hides from visitors)
    if (newStatus === 'draft') {
      setEventToUnpublish(item);
      setUnpublishDialogOpen(true);
      return;
    }
    await executeToggle(item.id, newStatus);
  };

  const handleConfirmUnpublish = async () => {
    if (!eventToUnpublish) return;
    await executeToggle(eventToUnpublish.id, 'draft');
    setUnpublishDialogOpen(false);
    setEventToUnpublish(null);
  };

  const executeToggle = async (eventId: string, newStatus: 'public' | 'draft') => {
    setTogglingId(eventId);
    try {
      await updateEvent.mutateAsync({ id: eventId, status: newStatus } as any);

      // Fire-and-forget: trigger AI-enhanced social draft when publishing
      if (newStatus === 'public') {
        supabase.functions.invoke('enhance-social-draft', {
          body: { source_type: 'event', source_id: eventId },
        }).catch(() => {});
      }

      toast.success(
        newStatus === 'public'
          ? 'Aktiviteten är nu synlig för besökare'
          : 'Aktiviteten är nu dold för besökare'
      );
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
      toast.error('Kunde inte ändra synlighet');
    } finally {
      setTogglingId(null);
    }
  };

  const hasItems = unified.length > 0;
  const producerPublicUrl = producer?.slug
    ? `${window.location.origin}${producerPublicPath(producer.slug)}`
    : null;
  const embedSrc = producer?.slug
    ? `${window.location.origin}${producerActivitiesEmbedPath(producer.slug)}`
    : null;
  const iframeSnippet = embedSrc
    ? `<iframe\n  src="${embedSrc}"\n  width="100%"\n  height="640"\n  style="border:0;"\n  loading="lazy"\n></iframe>`
    : null;

  return (
    <ProducerLayout farmId={farmId} producerName={producer?.name} title="Aktiviteter">
      <div data-testid="producer-events-page" className="w-full max-w-full overflow-x-hidden space-y-4">
       
        {/* Soft limit — bookable types only */}
        {usage && (
          <SoftLimitMessage
            used={bookableCount}
            limit={usage.events_limit}
            resourceName="bokningsbara aktiviteter"
            extraInfo="Utflykter är obegränsade. Uppgradera för att lägga till fler kurser och aktiviteter."
          />
        )}

        {/* ── Header ── */}
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-xl font-bold text-foreground">Aktiviteter</h1>
            <div className="flex items-center gap-2">
              <EntityShareAction
                entityType="producer"
                publicUrl={producerPublicUrl}
                embedSnippet={iframeSnippet}
              />
              <Button
                size="sm"
                className="hidden md:inline-flex"
                onClick={() => navigate(`/app/producer/farm/${farmId}/events/new`)}
              >
                <Plus className="h-4 w-4" />
                Skapa aktivitet
              </Button>
            </div>
          </div>
          {hasItems && (
            <p className="text-sm text-muted-foreground">
              {counts.published} publicerade
              {counts.drafts > 0 && ` · ${counts.drafts} utkast`}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : !hasItems ? (
          <>
            <EventsEmptyState farmId={farmId} hasDrafts={false} onCreateNew={openCreateMenu} />
            <CreateActivitySheet />
          </>
        ) : (
          <>
            {/* ── Filter chips ── */}
            <div className="w-full max-w-full overflow-hidden space-y-2">
              {/* Status filters */}
              <div className="flex flex-wrap gap-2">
                {STATUS_CHIPS.map((chip) => (
                  <button
                    key={chip.value}
                    type="button"
                    onClick={() => setActiveStatus(chip.value)}
                    className={cn(
                      "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                      "max-w-full truncate",
                      activeStatus === chip.value
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    )}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              {/* Type filters */}
              <div className="flex flex-wrap gap-2">
                {TYPE_CHIPS.map((chip) => (
                  <button
                    key={chip.value}
                    type="button"
                    onClick={() => setActiveType(chip.value)}
                    className={cn(
                      "rounded-full px-3 py-1.5 text-xs font-medium transition-colors border",
                      "max-w-full truncate",
                      activeType === chip.value
                        ? "bg-secondary text-secondary-foreground border-border"
                        : "bg-background text-muted-foreground border-border/50 hover:bg-muted/50"
                    )}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Only-drafts hint ── */}
            {counts.published === 0 && counts.drafts > 0 && activeStatus === 'all' && (
              <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
                Du har aktiviteter som inte är publicerade. Publicera dem så att besökare kan hitta dig i Farmly.
              </div>
            )}

            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Inga aktiviteter matchar filtret.
              </p>
            ) : (() => {
              // Split into upcoming and past for temporal grouping
              const upcoming = filtered.filter(item => !isPastActivity({ event_type: item.eventTypeRaw, timing_mode: item.timingMode, start_date: item.startDate, end_date: item.endDate }));
              const past = filtered.filter(item => isPastActivity({ event_type: item.eventTypeRaw, timing_mode: item.timingMode, start_date: item.startDate, end_date: item.endDate }));
              const showSections = activeStatus === 'all' && upcoming.length > 0 && past.length > 0;

              const renderCard = (item: typeof filtered[0], isPastItem: boolean, variant: 'default' | 'compact' = 'default') => (
                <ProducerEventCard
                  key={item.id}
                  item={item}
                  farmId={farmId}
                  hostName={producer?.name ?? 'Värden'}
                  producerSlug={producer?.slug}
                  producerIsPublic={producer?.status === 'public'}
                  isPast={isPastItem}
                  variant={variant}
                  isToggling={togglingId === item.id}
                  onToggleVisibility={() => handleToggleVisibility(item)}
                  onDelete={() => handleDeleteClick(item.id)}
                  onClone={() => handleClone(item)}
                />
              );

              if (showSections) {
                return (
                  <div className="space-y-8">
                    {/* Upcoming */}
                    <section>
                      <h2 className="text-sm font-medium text-muted-foreground mb-3">
                        Kommande aktiviteter
                        <span className="ml-1.5 text-xs text-muted-foreground/60">({upcoming.length})</span>
                      </h2>
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {upcoming.map(item => renderCard(item, false))}
                      </div>
                    </section>

                    {/* Past */}
                    <section>
                      <h2 className="text-sm font-medium text-muted-foreground/70 mb-3">
                        Tidigare aktiviteter
                        <span className="ml-1.5 text-xs text-muted-foreground/50">({past.length})</span>
                      </h2>
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {past.map(item => renderCard(item, true, 'compact'))}
                      </div>
                    </section>
                  </div>
                );
              }

              // Single flat grid (filter active or only one group)
              const isFilteredPast = activeStatus === 'past';
              return (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filtered.map(item => {
                    const pastItem = isPastActivity({ event_type: item.eventTypeRaw, timing_mode: item.timingMode, start_date: item.startDate, end_date: item.endDate });
                    return renderCard(item, pastItem, pastItem ? 'compact' : 'default');
                  })}
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort aktivitet?</AlertDialogTitle>
            <AlertDialogDescription>
              Det här kan inte ångras. Aktiviteten tas bort från Farmly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unpublish confirmation */}
      <AlertDialog open={unpublishDialogOpen} onOpenChange={(open) => {
        setUnpublishDialogOpen(open);
        if (!open) setEventToUnpublish(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dölj aktivitet?</AlertDialogTitle>
            <AlertDialogDescription>
              {eventToUnpublish && (
                <>
                  <strong>{eventToUnpublish.title}</strong> blir inte längre synlig för besökare på Farmly. Du kan publicera den igen när som helst.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmUnpublish}>
              Dölj aktivitet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ProducerLayout>
  );
}

// ─── Empty states ───────────────────────────────────────────────────
// UX rule: Pedagogical empty state. Inline create-button allowed here (exception to FAB-only rule).
// Använd "Aktivitet/Aktiviteter" konsekvent i UI.

function EventsEmptyState({ farmId, hasDrafts, onCreateNew }: { farmId: string; hasDrafts: boolean; onCreateNew: () => void }) {
  if (hasDrafts) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <Calendar className="h-12 w-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-semibold mb-1">Du har aktiviteter som inte är publicerade</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          Publicera dina utkast så att besökare kan hitta dig i Farmly.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <Calendar className="h-12 w-12 text-muted-foreground/40 mb-4" />
      <h3 className="text-lg font-semibold mb-1">Inga aktiviteter ännu</h3>
      <p className="text-sm text-muted-foreground mb-6 max-w-md">
        Skapa din första aktivitet för att synas i Farmly.
      </p>
      <Button onClick={onCreateNew}>
        <Plus className="h-4 w-4 mr-2" />
        Skapa ny aktivitet
      </Button>
    </div>
  );
}
